import { Box, Collapse, Divider } from '@wix/design-system';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useContext, useMemo } from 'react';
import { ArrowIcon } from '../arrowIcon/arrow-icon';
import { CollapseContext } from '../collapse-list/collapse-list';
import { useScrollIntoView } from '../collapse-list/scroll-into-view';
const DefaultWrapper = /*#__PURE__*/React.memo(_ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "D80",
    display: "block"
  }, children);
});
const HEADER_PADDING = {
  large: '15px 24px',
  medium: '16px',
  small: '12px 16px'
};
export const CollapseListItem = /*#__PURE__*/React.memo(_ref2 => {
  let {
    id,
    title,
    wrapper,
    variant = 'collapsible',
    disabled = false,
    dataHook,
    onToggle,
    onButtonClick,
    showContentDivider = false,
    prefix,
    subtitle,
    suffix,
    children,
    headerPadding = 'small',
    showTopShadow = false,
    showBottomShadow = false
  } = _ref2;
  const atoms = useContext(CollapseContext);
  if (!atoms) {
    throw new Error('CollapseListItem must be inside CollapseList.');
  }
  const {
    headerRef,
    contentRef,
    scrollWithDelay
  } = useScrollIntoView();
  const {
    toggleItemAtom,
    isItemOpenAtom
  } = atoms;
  const toggleItem = useSetAtom(toggleItemAtom);
  const isExpanded = useAtomValue(isItemOpenAtom(id));
  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }
    if (variant === 'collapsible') {
      toggleItem(id);
      onToggle == null || onToggle(id, !isExpanded);
    } else if (variant === 'button') {
      onButtonClick == null || onButtonClick(id);
    }
  }, [disabled, variant, toggleItem, id, onToggle, isExpanded, onButtonClick]);
  const renderHeader = useCallback(() => {
    return /*#__PURE__*/React.createElement(Box, {
      align: "space-between",
      direction: "horizontal",
      gap: "SP6",
      padding: HEADER_PADDING[headerPadding],
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Box, {
      direction: "horizontal",
      flexGrow: 1,
      gap: "SP3",
      verticalAlign: "middle",
      width: "65%"
    }, prefix, /*#__PURE__*/React.createElement(Box, {
      direction: "vertical",
      width: "100%"
    }, title, subtitle)), /*#__PURE__*/React.createElement(Box, {
      direction: "horizontal",
      gap: "SP3",
      justifySelf: "flex-end",
      verticalAlign: "middle"
    }, suffix, /*#__PURE__*/React.createElement(ArrowIcon, {
      disabled: disabled,
      isExpanded: isExpanded,
      variant: variant
    })));
  }, [headerPadding, prefix, title, subtitle, suffix, disabled, isExpanded, variant]);
  const boxShadow = useMemo(() => {
    const topShadow = '0px 8px 6px -6px rgba(0, 6, 36, 0.05) inset, 0px 22px 18px -18px rgba(0, 6, 36, 0.1) inset';
    const bottomShadow = '0px -8px 6px -6px rgba(0, 6, 36, 0.05) inset, 0px -22px 18px -18px rgba(0, 6, 36, 0.1) inset';
    if (showTopShadow && showBottomShadow) {
      return `${topShadow}, ${bottomShadow}`;
    }
    if (showTopShadow) {
      return topShadow;
    }
    if (showBottomShadow) {
      return bottomShadow;
    }
    return 'none';
  }, [showTopShadow, showBottomShadow]);
  const renderCollapseListContent = useCallback(() => {
    return /*#__PURE__*/React.createElement(Box, {
      dataHook: "collapse-list-item",
      direction: "vertical",
      overflow: "hidden"
    }, /*#__PURE__*/React.createElement("div", {
      "data-hook": dataHook,
      ref: headerRef,
      style: {
        cursor: disabled ? 'default' : 'pointer'
      },
      onClick: handleClick
    }, renderHeader()), variant === 'collapsible' && /*#__PURE__*/React.createElement(Collapse, {
      open: isExpanded,
      unmountOnExit: true,
      onExpandAnimationEnd: scrollWithDelay
    }, showContentDivider && /*#__PURE__*/React.createElement(Divider, {
      direction: "horizontal"
    }), /*#__PURE__*/React.createElement(Box, {
      boxShadow: boxShadow,
      display: "block"
    }, /*#__PURE__*/React.createElement("div", {
      ref: contentRef
    }, children))));
  }, [dataHook, disabled, handleClick, renderHeader, variant, isExpanded, showContentDivider, boxShadow, children, headerRef, contentRef, scrollWithDelay]);
  const Wrapper = wrapper || DefaultWrapper;
  return /*#__PURE__*/React.createElement(Wrapper, null, renderCollapseListContent());
});
//# sourceMappingURL=collapse-list-item.js.map